import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import CollectionPage from "./pages/CollectionPage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import AccountPage from "./pages/AccountPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import TrackOrderPage from "./pages/TrackOrderPage";
import B2BPage from "./pages/B2BPage";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import api from "./services/api";

// ─── Scroll to Top Component ───────────────────────────────────────────────
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [pathname]);

  return null;
}

// ─── App Context ───────────────────────────────────────────────────────────
const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

// ─── Toast ─────────────────────────────────────────────────────────────────
function ToastContainer({ toasts }) {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          style={{
            padding: "12px 20px",
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 14,
            background: t.type === "success" ? "#2d7a2d" : "#c0392b",
            color: "#fff",
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
            animation: "slideInRight 0.3s ease",
            maxWidth: 320,
          }}
        >
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ─── App Provider ──────────────────────────────────────────────────────────
export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("kkb_cart") || "[]");
    } catch {
      return [];
    }
  });

  const [wishlist, setWishlist] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("kkb_wishlist") || "[]");
    } catch {
      return [];
    }
  });

  const [searchOpen, setSearchOpen] = useState(false);
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts((p) => [...p, { id, message, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3500);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("kkb_token");
    if (!token) {
      setAuthLoading(false);
      return;
    }
    api.auth
      .me()
      .then((u) => {
        setUser(u);
        api.wishlist
          .get()
          .then((d) => {
            setWishlist(d.wishlist || []);
            localStorage.setItem(
              "kkb_wishlist",
              JSON.stringify(d.wishlist || []),
            );
          })
          .catch(() => {});
        api.cart
          .get()
          .then((d) => {
            setCart(d.items || []);
            localStorage.setItem("kkb_cart", JSON.stringify(d.items || []));
          })
          .catch(() => {});
      })
      .catch(() => localStorage.removeItem("kkb_token"))
      .finally(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    if (!user) localStorage.setItem("kkb_cart", JSON.stringify(cart));
  }, [cart, user]);

  const login = useCallback(async (email, password) => {
    const data = await api.auth.login({ email, password });
    localStorage.setItem("kkb_token", data.token);

    // data contains id, name, email, role, token directly (not data.user)
    setUser({
      id: data.id,
      name: data.name,
      email: data.email,
      role: data.role,
      phone: data.phone || "", // if login response doesn't include phone, fetch later
    });

    // Optionally fetch full profile (including phone, createdAt)
    try {
      const fullUser = await api.auth.me();
      setUser((prev) => ({ ...prev, ...fullUser }));
    } catch (e) {
      /* ignore */
    }

    // Sync guest cart
    const guestCart = JSON.parse(localStorage.getItem("kkb_cart") || "[]");
    if (guestCart.length) {
      try {
        const synced = await api.cart.sync(
          guestCart.map((i) => ({
            productId: i.productId,
            qty: i.qty,
            size: i.size,
          })),
        );
        setCart(synced.items || []);
      } catch {}
      localStorage.removeItem("kkb_cart");
    } else {
      const serverCart = await api.cart.get();
      setCart(serverCart.items || []);
    }

    const wl = await api.wishlist.get();
    setWishlist(wl.wishlist || []);

    return data;
  }, []);

  const register = useCallback(async (name, email, password, phone) => {
    const data = await api.auth.register({ name, email, password, phone });
    localStorage.setItem("kkb_token", data.token);
    setUser({
      id: data.id,
      name: data.name,
      email: data.email,
      role: data.role,
      phone: data.phone || phone,
    });
    return data;
  }, []);

  const logout = useCallback(async () => {
    await api.auth.logout().catch(() => {});
    localStorage.removeItem("kkb_token");
    localStorage.removeItem("kkb_cart");
    localStorage.removeItem("kkb_wishlist");
    setUser(null);
    setCart([]);
    setWishlist([]);
  }, []);

  const addToCart = useCallback(
    async (product, qty = 1, size = "Regular (2.0m)") => {
      const key = `${product.id}-${size}`;
      try {
        if (user) {
          const data = await api.cart.add(product.id, qty, size);
          setCart(data.items || []);
        } else {
          setCart((prev) => {
            const existing = prev.find((i) => i.key === key);
            if (existing)
              return prev.map((i) =>
                i.key === key ? { ...i, qty: i.qty + qty } : i,
              );
            return [
              ...prev,
              {
                key,
                productId: product.id,
                name: product.name,
                brand: product.brand,
                image: product.image,
                price: product.price,
                qty,
                size,
              },
            ];
          });
        }
        showToast(`${product.name} added to cart!`);
      } catch (err) {
        showToast(err.message || "Failed to add to cart", "error");
      }
    },
    [user, showToast],
  );

  const removeFromCart = useCallback(
    async (key) => {
      try {
        if (user) {
          const data = await api.cart.remove(key);
          setCart(data.items || []);
        } else {
          setCart((prev) => prev.filter((i) => i.key !== key));
        }
      } catch (err) {
        showToast(err.message || "Failed to remove item", "error");
      }
    },
    [user, showToast],
  );

  const updateQty = useCallback(
    async (key, qty) => {
      if (qty < 1) {
        removeFromCart(key);
        return;
      }
      try {
        if (user) {
          const data = await api.cart.update(key, qty);
          setCart(data.items || []);
        } else {
          setCart((prev) =>
            prev.map((i) => (i.key === key ? { ...i, qty } : i)),
          );
        }
      } catch (err) {
        showToast(err.message || "Failed to update quantity", "error");
      }
    },
    [user, removeFromCart, showToast],
  );

  const clearCart = useCallback(async () => {
    try {
      if (user) await api.cart.clear();
      setCart([]);
      localStorage.removeItem("kkb_cart");
    } catch {}
  }, [user]);

  const toggleWishlist = useCallback(
    async (productId) => {
      try {
        if (user) {
          const data = await api.wishlist.toggle(productId);
          setWishlist(data.wishlist || []);
          localStorage.setItem(
            "kkb_wishlist",
            JSON.stringify(data.wishlist || []),
          );
          showToast(
            data.action === "added"
              ? "Added to wishlist ❤️"
              : "Removed from wishlist",
          );
        } else {
          setWishlist((prev) => {
            const next = prev.includes(productId)
              ? prev.filter((id) => id !== productId)
              : [...prev, productId];
            localStorage.setItem("kkb_wishlist", JSON.stringify(next));
            showToast(
              next.includes(productId)
                ? "Added to wishlist ❤️"
                : "Removed from wishlist",
            );
            return next;
          });
        }
      } catch (err) {
        showToast(err.message || "Failed to update wishlist", "error");
      }
    },
    [user, showToast],
  );

  const cartCount = cart.reduce((s, i) => s + (i.qty || 0), 0);
  const cartTotal = cart.reduce((s, i) => s + (i.price || 0) * (i.qty || 0), 0);

  const value = {
    user,
    authLoading,
    login,
    register,
    logout,
    cart,
    cartCount,
    cartTotal,
    addToCart,
    removeFromCart,
    updateQty,
    clearCart,
    wishlist,
    toggleWishlist,
    searchOpen,
    setSearchOpen,
    showToast,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} />
    </AppContext.Provider>
  );
}

// ─── Wrapper Component to provide navigate ─────────────────────────────────
function AppWithNavigate() {
  const navigate = useNavigate();

  return (
    <AppProvider>
      <ScrollToTop />
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/collection" element={<CollectionPage />} />
          <Route path="/product/:id" element={<ProductPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/track" element={<TrackOrderPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/b2b" element={<B2BPage navigate={navigate} />} />
        </Routes>
      </main>
      <Footer navigate={navigate} />
    </AppProvider>
  );
}

// ─── App ───────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <AppWithNavigate />
    </BrowserRouter>
  );
}
